// 作業中データの共有：人の判断が要るときだけ出る帯（本城さん要望 2026-08-06）
//
// 普段は出ない。出るのは「勝手に決めてはいけない」場面だけ:
//   remote  … 他の方が更新した。手元にも未保存の変更がある
//   startup … 起動時、共有の内容と手元の内容が食い違っていた
//   empty   … 明細0件で、明細のある共有データを上書きしようとした（ガードが止めた）
//
// どちらを採るかは必ず人が選ぶ（＝検出は自動、判断は人間）。両方とも取り返しがつかないため確認を挟む。
//
// もう1つ、「共有への保存が失敗したまま」が一定時間続いたときも帯を出す（2026-09-07 本城さん報告から）。
// それまではヘッダの黄色いピル「共有していません」だけだったため、徳永さんが取り込んだ8月分が
// 誰にも届いていないことに、本人も本城さんも気づけなかった。

import React, { useEffect, useState } from "react";
import { Ic } from "./Icons";
import { useStore } from "../store";

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const min = Math.floor((Date.now() - t) / 60000);
  if (min < 1) return "たった今";
  if (min < 60) return `${min}分前`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}時間前`;
  return new Date(iso).toLocaleString("ja-JP");
}

// 保存失敗がこれだけ続いたら帯で知らせる（ポーリング20秒×数回の自動再保存で直らなかった、の意味）
const SHARE_STUCK_MS = 2 * 60 * 1000;

export function ShareBanner() {
  const { share, shareConflict, loadShared, overwriteShared } = useStore();
  const [busy, setBusy] = useState<"load" | "push" | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 「共有していません」が始まった時刻。競合以外のエラーが続いているかの判定に使う。
  // 自動再送のたびに error → saving → error と往復するので、saving では起点を消さない
  // （消すと帯が永遠に出ない）。synced など「保存できた」状態になったときだけ消す。
  const [errorSince, setErrorSince] = useState<number | null>(null);
  const [, setPulse] = useState(0);
  useEffect(() => {
    if (share.mode === "error") setErrorSince((v) => v ?? Date.now());
    else if (share.mode !== "saving") setErrorSince(null);
  }, [share.mode]);
  useEffect(() => {
    if (errorSince == null) return;
    const id = window.setInterval(() => setPulse((n) => n + 1), 30 * 1000);
    return () => window.clearInterval(id);
  }, [errorSince]);

  const stuck = share.mode === "error" && !shareConflict && errorSince != null && Date.now() - errorSince >= SHARE_STUCK_MS;
  if (stuck) {
    return (
      <div className="share-banner">
        <Ic.alert size={17} />
        <div className="share-banner-text">
          <div className="share-banner-title">
            共有に保存できていません{share.error ? `（${share.error}）` : ""}。この内容は、まだこの端末にだけあります。
          </div>
          <div className="tiny">
            他の方の画面には出ていません。自動で保存し直していますが、この表示が消えない場合はページを再読み込みしてください
            （作業中の内容はこの端末に残ります）。
          </div>
        </div>
        <div className="row gap-8" style={{ flexShrink: 0, alignItems: "center" }}>
          <button className="btn sm primary" onClick={() => window.location.reload()}>
            ページを再読み込み
          </button>
        </div>
      </div>
    );
  }

  if (!shareConflict) return null;
  const meta = shareConflict.meta;
  const who = meta?.savedBy || "他の方";
  const when = relTime(meta?.savedAt);

  const headline =
    shareConflict.reason === "remote"
      ? `${who} が ${when} に共有を更新しました。この端末にも保存していない変更があります。`
      : shareConflict.reason === "overwritten"
      ? `この端末が保存した内容が、${who} の保存（${when}）で上書きされました。ほぼ同時に保存すると起こります。`
      : shareConflict.reason === "empty"
      ? `共有には ${meta?.count ?? "—"} 件の明細があります。この端末は0件のため、上書きを止めました。`
      : `共有されている内容と、この端末の内容が違います。${who} が ${when} に更新（${meta?.count ?? "—"} 件）。`;

  const doLoad = async () => {
    if (!window.confirm("共有の最新を読み込みます。\nこの端末で行った未保存の変更は失われます。\n\n続けますか？")) return;
    setBusy("load");
    setError(null);
    const r = await loadShared();
    setBusy(null);
    if (!r.ok) setError(r.error || "読み込みに失敗しました");
  };

  // 上書きは他の方の作業を消す操作なので、確認を2段にして「間違えて押した」を通さない
  const doOverwrite = async () => {
    if (
      !window.confirm(
        `この端末の内容で共有を上書きしようとしています。\n\n${who} が保存した内容は消え、全員の画面がこの端末の内容に置き換わります。\n通常は「共有の最新を表示する」をお選びください。\n\n上書きに進みますか？`
      )
    )
      return;
    if (!window.confirm(`最終確認です。\n${who} の更新（${when}）を消して、この端末の内容で上書きします。\n\n本当によろしいですか？`)) return;
    setBusy("push");
    setError(null);
    const r = await overwriteShared();
    setBusy(null);
    if (!r.ok) setError(r.error || "保存に失敗しました");
  };

  return (
    <div className="share-banner">
      <Ic.alert size={17} />
      <div className="share-banner-text">
        <div className="share-banner-title">{headline}</div>
        <div className="tiny">
          迷った場合は「共有の最新を表示する」を選んでください。選ぶまで、この端末の変更は共有へ保存されません。
          {error && <span style={{ color: "var(--red)", fontWeight: 700 }}> ／ {error}</span>}
        </div>
      </div>
      <div className="row gap-8" style={{ flexShrink: 0, alignItems: "center" }}>
        <button className="btn sm primary" onClick={doLoad} disabled={busy !== null}>
          {busy === "load" ? "読み込み中..." : "共有の最新を表示する（おすすめ）"}
        </button>
        <button
          className="btn sm ghost"
          onClick={doOverwrite}
          disabled={busy !== null}
          title={`${who} の更新を消して、この端末の内容を全員に反映します。通常は使いません。`}
        >
          {busy === "push" ? "保存中..." : "この端末の内容で上書き..."}
        </button>
      </div>
    </div>
  );
}
